/**
 * header.js — dostępny nagłówek strony (WCAG 2.1 AA)
 *
 * Kluczowe zmiany względem oryginału:
 *
 * 1. Konwersja z Class Component na Functional Component z hookami.
 *
 * 2. Poprawna semantyka HTML:
 *    - <header role="banner"> — landmark dla czytników ekranu
 *    - <nav aria-label="..."> — landmark nawigacji (dwa: desktop + mobilna)
 *    - Logo: <Link aria-label="...strona główna">, img alt="" (dekoracyjna)
 *
 * 3. Naprawiony krytyczny błąd HTML:
 *    ORYGINAŁ: <Link> (<a>) zawierał <ul> z kolejnymi <Link> (<a>).
 *    Zagnieżdżone <a> to invalid HTML — dezorientuje czytniki ekranu.
 *    POPRAWKA: oddzielny <button> jako wyzwalacz dropdown (wzorzec
 *    WAI-ARIA "Disclosure Button"), <ul> jest rodzeństwem, nie dzieckiem.
 *
 * 4. Pełna nawigacja klawiaturą:
 *    - Tab/Shift+Tab — poruszanie po elementach
 *    - Enter/Spacja na <button> — otwiera/zamyka podmenu
 *    - Escape — zamyka podmenu, focus wraca do triggera
 *    - onBlur — auto-zamknięcie gdy focus opuszcza element
 *
 * 5. Wszystkie elementy interaktywne mają ARIA:
 *    - aria-expanded, aria-controls na triggerach dropdown (wzorzec disclosure)
 *    - aria-hidden="true" na SVG dekoracyjnych
 *    - aria-label na burgerze i linku telefonu
 *    - aria-hidden na zamkniętym menu mobilnym
 *
 * 6. Widget dostępności (AccessibilityToolbar) wbudowany w header.
 */

import React, { useState, useRef, useEffect, useCallback } from "react"
import { Link } from "gatsby"

// ─── Dane nawigacji ───────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { href: "/uslugi/",      title: "Usługi",      submenu: null },
  { href: "/aktualnosci/", title: "Aktualności", submenu: null },
  {
    href: "/diagnostyka/",
    title: "Diagnostyka",
    submenu: [
      { href: "/diagnostyka/audiometria/",              title: "Audiometria" },
      { href: "/diagnostyka/ekg/",                      title: "EKG" },
      { href: "/diagnostyka/rtg/",                      title: "RTG" },
      { href: "/diagnostyka/densytometria/",            title: "Densytometria" },
      { href: "/diagnostyka/pracownia-kardiologiczna/", title: "Pracownia kardiologiczna" },
      { href: "/diagnostyka/laboratorium/",             title: "Laboratorium" },
      { href: "/diagnostyka/spirometria/",              title: "Spirometria" },
      { href: "/diagnostyka/usg/",                      title: "USG" },
    ],
  },
  {
    href: "/poradnie/",
    title: "Poradnie",
    submenu: [
      {
        heading: "Podstawowa opieka zdrowotna",
        items: [
          { href: "/poradnie/poradnia-medycyny-rodzinnej/", title: "Poradnia medycyny rodzinnej" },
          { href: "/poradnie/poradnia-pediatryczna/",       title: "Poradnia pediatryczna" },
        ],
      },
      {
        heading: "Poradnie specjalistyczne NFZ",
        items: [
          { href: "/poradnie/poradnia-ginekologiczna/",  title: "Ginekologiczna" },
          { href: "/poradnie/poradnia-stomatologiczna/", title: "Stomatologiczna" },
          { href: "/poradnie/poradnia-urologiczna/",     title: "Urologiczna" },
        ],
      },
      {
        heading: "Poradnie specjalistyczne prywatne",
        items: [
          { href: "/poradnie/poradnia-kardiologiczna/",           title: "Kardiologiczna" },
          { href: "/poradnie/prywatna-poradnia-stomatologiczna/", title: "Stomatologiczna prywatna" },
        ],
      },
    ],
  },
  {
    href: "/projekty-unijne/",
    title: "Projekty unijne",
    submenu: [
      { href: "/ddom/",                           title: "DDOM" },
      { href: "/projekty-unijne/informatyzacja/", title: "Informatyzacja" },
      { href: "/projekty-unijne/wypozyczalnia/",  title: "Wypożyczalnia sprzętu" },
      { href: "/adaptacja-wysokiego-parteru/",    title: "Adaptacja parteru" },
    ],
  },
  { href: "/kontakt/", title: "Kontakt", submenu: null },
]

// ─── Pojedynczy element nawigacji desktopowej ─────────────────────────────────
function NavItem({ item }) {
  const [isOpen, setIsOpen]   = useState(false)
  const containerRef          = useRef(null)
  const triggerRef            = useRef(null)
  const submenuId = `nav-sub-${item.href.replace(/\//g, "-").replace(/^-|-$/g, "")}`
  const isGrouped = item.submenu && item.submenu[0]?.heading

  // Auto-zamknięcie gdy focus opuszcza cały blok li
  const handleBlur = useCallback((e) => {
    if (!containerRef.current?.contains(e.relatedTarget)) {
      setIsOpen(false)
    }
  }, [])

  // Escape zamyka i wraca focus do triggera
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape" && isOpen) {
      e.stopPropagation()
      setIsOpen(false)
      triggerRef.current?.focus()
    }
  }, [isOpen])

  // Zamknięcie kliknięciem poza elementem
  useEffect(() => {
    if (!isOpen) return
    const close = (e) => {
      if (!containerRef.current?.contains(e.target)) setIsOpen(false)
    }
    document.addEventListener("mousedown", close)
    return () => document.removeEventListener("mousedown", close)
  }, [isOpen])

  // Brak podmenu — prosty link
  if (!item.submenu) {
    return (
      <li className="a-menu-item a-menu-item--horizontal">
        <Link to={item.href} activeClassName="is-active">
          {item.title}
        </Link>
      </li>
    )
  }

  return (
    <li
      ref={containerRef}
      className={`a-menu-item a-menu-item--horizontal a-menu-item--has-submenu${isOpen ? " is-open" : ""}`}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {/* Link do strony nadrzędnej */}
      <Link to={item.href} activeClassName="is-active" onBlur={handleBlur}>
        {item.title}
      </Link>

      {/*
        Oddzielny przycisk otwierający podmenu (WAI-ARIA: Disclosure Button).
        DLACZEGO oddzielny: link prowadzi do strony, button otwiera menu.
        Dwie różne akcje = dwa osobne elementy interaktywne.
      */}
      <button
        ref={triggerRef}
        type="button"
        className="a-menu-item__toggle"
        aria-expanded={isOpen}
        aria-controls={submenuId}
        aria-label={`${isOpen ? "Zwiń" : "Rozwiń"} podmenu ${item.title}`}
        onClick={() => setIsOpen(p => !p)}
        onBlur={handleBlur}
      >
        <svg
          width="8" height="5"
          viewBox="0 0 8 5"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
          className={isOpen ? "is-rotated" : ""}
        >
          <path
            d="M1 1L4 4L7 1"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Podmenu płaskie (np. Diagnostyka) */}
      {!isGrouped && (
        <ul id={submenuId} className="m-vertical-menu" role="list">
          {item.submenu.map((sub) => (
            <li key={sub.href} className="a-menu-item a-menu-item--vertical">
              <Link to={sub.href} activeClassName="is-active" onBlur={handleBlur}>
                {sub.title}
              </Link>
            </li>
          ))}
        </ul>
      )}

      {/* Podmenu zgrupowane (np. Poradnie) */}
      {isGrouped && (
        <div id={submenuId} className="wide-menu">
          {item.submenu.map((group) => (
            <div key={group.heading} className="wide-menu__group">
              <h3 className="wide-menu__heading">{group.heading}</h3>
              <ul>
                {group.items.map((sub) => (
                  <li key={sub.href} className="a-menu-item a-menu-item--vertical">
                    <Link to={sub.href} activeClassName="is-active" onBlur={handleBlur}>
                      {sub.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </li>
  )
}

// ─── Główny komponent Header ──────────────────────────────────────────────────
export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const mobileNavRef  = useRef(null)
  const burgerRef     = useRef(null)
  const MOBILE_NAV_ID = "mobile-navigation"

  // Escape zamyka menu mobilne, focus wraca na burger
  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMobileOpen(false)
        burgerRef.current?.focus()
      }
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [mobileOpen])

  // Focus na pierwszym linku po otwarciu menu mobilnego
  useEffect(() => {
    if (mobileOpen) {
      const first = mobileNavRef.current?.querySelector("a")
      first?.focus()
    }
  }, [mobileOpen])

  // Blokada scrolla gdy menu mobilne otwarte
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : ""
    return () => { document.body.style.overflow = "" }
  }, [mobileOpen])

  return (
    <>
      <header className="o-header" role="banner">
        <div className="o-header__wrapper" data-g="container">

          {/* Logo */}
          <div className="a-logo">
            <Link to="/" aria-label="Medicus-Dukla — strona główna">
              {/* img alt="" — tekst linka niesiony przez aria-label na <Link> */}
              <img
                src="/uploads/logo.svg"
                alt=""
                role="presentation"
                height={30}
                width="auto"
              />
            </Link>
          </div>

          {/* Nawigacja desktopowa */}
          <nav
            className="o-header__nav"
            aria-label="Główna nawigacja"
            data-g="hide show@lg"
          >
            <ul className="m-nav" role="list">
              {NAV_ITEMS.map((item) => (
                <NavItem key={item.href} item={item} />
              ))}
              <li className="a-menu-telephone">
                {/*
                  Nazwa dostępna zaczyna się od widocznego tekstu numeru
                  (WCAG 2.5.3 Label in Name) — użytkownicy sterowania głosem
                  mogą aktywować link, wymawiając widoczny numer. Doprecyzowanie
                  ("zadzwoń do przychodni") dodane jako tekst sr-only PO numerze.
                */}
                <a href="tel:+48134330328">
                  <img
                    src="/images/layout/phone.svg"
                    alt=""
                    aria-hidden="true"
                    width={16}
                    height={16}
                  />
                  <span>13&nbsp;433&nbsp;03&nbsp;28</span>
                  <span className="sr-only">, zadzwoń do przychodni</span>
                </a>
              </li>
            </ul>
          </nav>

          {/* Burger — <button> zamiast <div> (WCAG 4.1.2) */}
          <button
            ref={burgerRef}
            type="button"
            className={`burger${mobileOpen ? " active" : ""}`}
            onClick={() => setMobileOpen(p => !p)}
            aria-expanded={mobileOpen}
            aria-controls={MOBILE_NAV_ID}
            aria-label={mobileOpen ? "Zamknij menu nawigacji" : "Otwórz menu nawigacji"}
            data-g="show hide@lg"
          >
            <span className="line" aria-hidden="true" />
            <span className="line" aria-hidden="true" />
            <span className="line" aria-hidden="true" />
          </button>
        </div>
      </header>

      {/* Nawigacja mobilna */}
      <nav
        id={MOBILE_NAV_ID}
        ref={mobileNavRef}
        className={`o-mobile-nav${mobileOpen ? " active" : ""}`}
        aria-label="Nawigacja mobilna"
          data-g="show hide@lg"
      >
        <ul className="m-nav" data-g="container" role="list">
          {NAV_ITEMS.map((item) => (
            <li key={item.href} className="a-menu-item a-menu-item--horizontal">
              <Link to={item.href} onClick={() => setMobileOpen(false)}>
                {item.title}
              </Link>
            </li>
          ))}
        </ul>

        {/* Dekoracja — ukryta dla AT */}
        <svg
          className="decoration"
          xmlns="http://www.w3.org/2000/svg"
          width="156"
          height="149"
          viewBox="0 0 156 149"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="24" cy="125" r="24" fill="#404E7C" />
          <path d="M57 99C111.686 99 156 54.6857 156 -4.71837e-06L57 0L57 99Z" fill="#00B8BC" />
          <path
            d="M106.5 99C79.1571 99 57 121.157 57 148.5L156 148.5C156 121.157 133.843 99 106.5 99Z"
            fill="#EAC4D5"
          />
        </svg>
      </nav>
    </>
  )
}
